/** Deleting a manuscript (from its Details tab or from the Archive), and archiving one. */
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Trash } from "lucide-react";
import { api, errorMessage, post } from "@/lib/api";
import { qk } from "@/lib/queries";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { mqk } from "./types";

type Ms = { id: string; title: string; workspaceId: string };

async function refresh(qc: ReturnType<typeof useQueryClient>, ms: Ms) {
  await Promise.all([
    qc.invalidateQueries({ queryKey: qk.manuscripts(ms.workspaceId) }),
    qc.invalidateQueries({ queryKey: qk.tree(ms.workspaceId) }),
    qc.invalidateQueries({ queryKey: ["archive"] }), // the Archive page (archiveKey; not imported to avoid an import cycle)
    qc.invalidateQueries({ queryKey: mqk.detail(ms.id) }),
    qc.invalidateQueries({ queryKey: ["note"] }),
  ]);
}

export async function setManuscriptArchived(qc: ReturnType<typeof useQueryClient>, ms: Ms, archived: boolean) {
  try {
    await post(`/api/manuscripts/${ms.id}/${archived ? "archive" : "unarchive"}`, {});
    await refresh(qc, ms);
    toast.success(archived ? `“${ms.title}” is archived: find it in the Archive.` : `“${ms.title}” is back.`);
    return true;
  } catch (e) {
    toast.error(errorMessage(e));
    return false;
  }
}

export function DeleteManuscriptDialog({ manuscript: ms, onClose, onDeleted }: { manuscript: Ms; onClose(): void; onDeleted?(): void }) {
  const qc = useQueryClient();
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Delete “{ms.title}”?</DialogTitle>
          <DialogDescription>
            The manuscript, its AI and journal reviews, its revision table and its submission snapshots are deleted for good. Its section notes go to the Trash, where you can
            still restore them.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1">
          <Label htmlFor="ms-delete-confirm">Type the title to confirm</Label>
          <Input id="ms-delete-confirm" value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={typed.trim() !== ms.title.trim() || busy}
            onClick={async () => {
              setBusy(true);
              try {
                await api(`/api/manuscripts/${ms.id}`, { method: "DELETE", body: { confirmTitle: typed } });
                toast.success(`“${ms.title}” was deleted. Its section notes are in the Trash.`);
                await refresh(qc, ms);
                onClose();
                onDeleted?.();
              } catch (e) {
                toast.error(errorMessage(e));
              } finally {
                setBusy(false);
              }
            }}
          >
            <Trash /> Delete manuscript
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
